<template>
  <v-popover
    v-if="showComponent"
    interactive
    placement="bottom-start"
    content-class="templates-dropdown-popover"
    class="flex overflow-hidden"
    :trigger="!templatesFeatureAvailable ? 'manual' : 'click'"
    :role="'poper'"
    :append-to="bodyRef"
    :on-trigger="(i, e) => e.stopPropagation()"
    :on-untrigger="(i, e) => e.stopPropagation()"
    @state="dropdownStateChange"
  >
    <template #default>
      <ButtonCustom
        v-tooltip="{
          content: translate('applyTemplate'),
          onShow: () => !!enableTooltip,
          placement: 'bottom',
        }"
        class="apply-template-button"
        :class="[
          buttonCustomClass,
          { 'p-1': hideButtonText },
          activeStateClass,
          { 'safari-hidden pr-0': isSafariCheck },
        ]"
        :type="'transparent'"
        :text="translate('applyTemplate')"
        :no-label="hideButtonText"
        :custom-icon-name="showButtonIcon ? 'template' : ''"
        custom-icon-color=""
        custom-icon-size="16px"
        @click="handleOnShow"
      />
    </template>
    <template #content="{ state, hide }">
      <TemplatesDropdown
        v-if="state.isVisible"
        :selected-template-id="selectedTemplateId"
        :resource-type="resourceType"
        :editor-id="editorId"
        :filter-templates="filterTemplates"
        @selectTemplate="selectTemplate($event, hide)"
        @hidePopup="hide"
      />
    </template>
  </v-popover>
</template>

<script>
import ButtonCustom from '@/components/elements/ButtonCustom';
import TemplatesDropdown from '@/components/widgets/TemplatesDropdown';
import { ResourceType } from '@/types/resources';
import { createNewPageAndUpdateRoute } from '@/utilities/createNewPageAndUpdateRoute';
import updatePage from '@/mixins/pages/updatePage';
import { generateEditorId } from '@/utilities/generateEditorId';

export default {
  name: 'ApplyTemplate',
  components: {
    ButtonCustom,
    TemplatesDropdown,
  },

  mixins: [updatePage],

  props: {
    resourceType: {
      type: String,
      required: false,
      default: ResourceType.Card,
    },
    showButtonIcon: {
      type: Boolean,
      required: false,
      default: true,
    },
    hideButtonText: {
      type: Boolean,
      required: false,
      default: false,
    },
    buttonCustomClass: {
      type: String,
      required: false,
      default: '',
    },
    enableTooltip: {
      type: Boolean,
      required: false,
      default: false,
    },

    pageViewTemplate: {
      type: Boolean,
      required: false,
      default: false,
    },

    isTemplate: {
      type: Boolean,
      required: false,
      default: false,
    },

    templateId: {
      type: String,
      required: false,
      default: '',
    },

    isQuickCardModal: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['selectTemplate', 'click', 'hideTemplatePopup', 'dropdownStateChange'],

  setup() {
    return {
      bodyRef: document.body,
    };
  },
  data() {
    return {
      selectedTemplateId: '',
      showComponent: false,
      isPopoverActive: false,
    };
  },
  computed: {
    filterTemplates() {
      return this.isTemplate ? [this.templateId] : [];
    },
    isCardTemplate() {
      return this.resourceType === ResourceType.Card;
    },
    templates() {
      return this.isCardTemplate
        ? this.$store.getters.getCardTemplates
        : this.$store.getters.getPageTemplates;
    },
    buttonText() {
      return this.hideButtonText ? this.translate('applyTemplate') : '';
    },
    activeStateClass() {
      if (!this.isPopoverActive) return '';
      return this.hideButtonText ? 'bg-important-hover' : 'bg-hover-default';
    },

    editorId() {
      return generateEditorId(this.resourceType, this.currentPageId, this.currentTeamId);
    },

    editor() {
      return this.$store.getters.editor(this.editorId) || {};
    },

    currentPageId() {
      return this.$store.getters.getCurrentPageID;
    },

    currentTeamId() {
      return this.$store.getters.getTeamID;
    },

    templatesFeatureAvailable() {
      return this.$store.getters.getTeamFeatureEnabled(
        this.$constants.featureFlagIds.cardAndPageTemplates
      );
    },
  },
  created() {
    this.fetchTemplates().then(() => {
      this.showComponent = true;
    });
  },

  methods: {
    handleOnShow() {
      if (!this.templatesFeatureAvailable) {
        this.$store.dispatch(
          'setShowUpgradePlanPopup',
          this.$constants.featureFlagIds.cardAndPageTemplates
        );
      }
    },
    dropdownStateChange(state) {
      this.isPopoverActive = state.isVisible;
      this.$emit('dropdownStateChange', state.isVisible);
    },
    selectTemplate({ template, isEnterKey }, hideFn) {
      if (typeof hideFn === 'function') hideFn();

      const selectedTemplate = this.templates[template.id];

      if (this.isTemplate && !this.isQuickCardModal) {
        if (selectedTemplate?.card_child_cards?.length) {
          this.$store.dispatch('addUiNotification', {
            message: this.translate('templateWithChildCardsNotAllowed'),
            status: this.$constants.uiNotificationStatuses.error,
            duration: 4000,
          });
          return;
        }
      }

      this.selectedTemplateId = template.id;
      if (this.pageViewTemplate) {
        // add logic to select template in editor

        if (this.resourceType === ResourceType.Page) {
          this.applyTemplateToPage(selectedTemplate);
        }
      }
      this.$emit('selectTemplate', selectedTemplate, isEnterKey);
    },
    fetchTemplates() {
      const action = this.isCardTemplate ? 'fetchCardTemplates' : 'fetchPageTemplates';
      return this.$store.dispatch(action);
    },

    applyTemplateToPage(selectedTemplate) {
      const newPage = {
        ...(selectedTemplate.page_title && { title: selectedTemplate.page_title }),
        content: selectedTemplate.page_content,
      };
      // creating a new page
      if (this.$route.name === this.$constants.newPage) {
        this.$store.dispatch('setPage', newPage);
        this.$store.dispatch('setPageEditorDisplay', { content: newPage.content });
        createNewPageAndUpdateRoute(newPage).then((page) => {
          this.$store.dispatch('setPage', page);
        });
      } else {
        // if page already exists
        // you can still apply template to it if content is empty
        const tr = this.editor.view.state.tr;
        tr.setMeta('contentMenu', {
          type: 'remove',
        });
        this.editor.view.dispatch(tr);
        this.editor.commands.setContent(newPage.content);
        Object.keys(newPage).forEach((key) => {
          this.$store.dispatch('localUpdatePageProp', {
            pageId: this.currentPageId,
            propertyName: key,
            value: newPage[key],
          });
        });
        this.updatePage({
          pageId: this.currentPageId,
          payload: { title: newPage.title, content: newPage.content },
        })
          .then(({ page }) => {
            this.$store.dispatch('setPage', page);
            this.editor.commands.enter();
          })
          .finally(() => {
            const transaction = this.editor.view.state.tr;
            transaction.setMeta('contentMenu', {
              type: 'preventDecorationCreation',
              value: false,
            });
            this.editor.view.dispatch(transaction);
          });
      }
    },
  },
};
</script>
